// Propriedades de cada tipo de bloco no painel do editor.
import type { LandingBlock } from '@impulso/shared/landing'
import { AreaField, BgField, ButtonField, Choice, GalleryField, ImageField, LinesField, ListField, TextField, Toggle } from './fields'

export interface BlockFormProps {
  block: LandingBlock
  onChange: (b: LandingBlock) => void
  companyId: string
  canEdit: boolean
  /** WhatsApp padrão para botões novos */
  phone: string
}

export default function BlockForm({ block, onChange, companyId, canEdit, phone }: BlockFormProps) {
  // `set` aceita só campos do tipo atual do bloco
  const set = <B extends LandingBlock>(b: B, patch: Partial<B>) => onChange({ ...b, ...patch })
  const img = { companyId, canEdit }
  const body = (() => {
    switch (block.type) {
      case 'header':
        return <>
          <TextField label="Nome do negócio" value={block.name} max={120} onChange={(v) => set(block, { name: v })} />
          <ImageField label="Logo (opcional, substitui o nome)" {...img} value={block.logo} onChange={(v) => set(block, { logo: v })} />
          <ButtonField label="Botão" optional defaultPhone={phone} value={block.button} onChange={(v) => set(block, { button: v })} />
        </>
      case 'hero':
        return <>
          <Choice label="Formato" value={block.layout} onChange={(v) => set(block, { layout: v })} options={[{ value: 'split', label: 'Texto + foto' }, { value: 'center', label: 'Centralizado' }, { value: 'cover', label: 'Foto de fundo' }]} />
          <TextField label="Chamada pequena (acima do título)" value={block.eyebrow} max={80} placeholder="Ex.: Atendimento em São Paulo" onChange={(v) => set(block, { eyebrow: v })} />
          <AreaField label="Título" rows={2} max={200} value={block.title} onChange={(v) => set(block, { title: v })} hint="Diga o principal benefício em poucas palavras." />
          <AreaField label="Frase de apoio" rows={3} max={500} value={block.subtitle} onChange={(v) => set(block, { subtitle: v })} />
          <LinesField label="Tópicos com ✓" value={block.bullets} onChange={(v) => set(block, { bullets: v })} placeholder="Ex.: Entrega no mesmo dia" />
          <ButtonField label="Botão principal" optional defaultPhone={phone} value={block.primary} onChange={(v) => set(block, { primary: v })} />
          <ButtonField label="Botão secundário" optional defaultPhone={phone} value={block.secondary} onChange={(v) => set(block, { secondary: v })} />
          <ImageField label={block.layout === 'cover' ? 'Foto de fundo' : block.layout === 'center' ? 'Foto redonda (opcional)' : 'Foto'} {...img} value={block.image} onChange={(v) => set(block, { image: v })} />
        </>
      case 'text':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <AreaField label="Texto" rows={8} value={block.body} onChange={(v) => set(block, { body: v })} hint="Deixe uma linha em branco para começar outro parágrafo. Use **assim** para negrito." />
          <Choice label="Alinhamento" value={block.align} onChange={(v) => set(block, { align: v })} options={[{ value: 'left', label: 'Esquerda' }, { value: 'center', label: 'Centro' }]} />
        </>
      case 'features':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <TextField label="Subtítulo" value={block.subtitle} max={400} onChange={(v) => set(block, { subtitle: v })} />
          <Choice label="Colunas" value={block.columns} onChange={(v) => set(block, { columns: v })} options={[{ value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }]} />
          <ListField label="Itens" items={block.items} onChange={(v) => set(block, { items: v })} itemTitle={(i) => `${i.icon} ${i.title}`}
            create={() => ({ icon: '✅', title: 'Novo benefício', text: 'Explique em uma frase.' })}
            render={(i, u) => <>
              <TextField label="Ícone (emoji)" value={i.icon} max={16} onChange={(v) => u({ icon: v })} hint="Ex.: ⭐ 🚚 💬 🛡️ ⏱️" />
              <TextField label="Título" value={i.title} max={120} onChange={(v) => u({ title: v })} />
              <AreaField label="Texto" rows={2} max={500} value={i.text} onChange={(v) => u({ text: v })} />
            </>} />
        </>
      case 'image_text':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <AreaField label="Texto" rows={6} value={block.body} onChange={(v) => set(block, { body: v })} hint="Use **assim** para negrito." />
          <Choice label="Foto do lado" value={block.side} onChange={(v) => set(block, { side: v })} options={[{ value: 'left', label: 'Esquerdo' }, { value: 'right', label: 'Direito' }]} />
          <ImageField label="Foto" {...img} value={block.image} onChange={(v) => set(block, { image: v })} />
          <ButtonField label="Botão" optional defaultPhone={phone} value={block.button} onChange={(v) => set(block, { button: v })} />
        </>
      case 'gallery':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <GalleryField {...img} value={block.images} onChange={(v) => set(block, { images: v })} />
        </>
      case 'testimonials':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <ListField label="Depoimentos" items={block.items} onChange={(v) => set(block, { items: v })} itemTitle={(i) => i.name}
            create={() => ({ name: 'Nome do cliente', detail: '', text: 'O que ele disse sobre você.', rating: 5 })}
            render={(i, u) => <>
              <TextField label="Nome" value={i.name} max={120} onChange={(v) => u({ name: v })} />
              <TextField label="Detalhe (opcional)" value={i.detail} max={120} placeholder="Ex.: Cliente desde 2022" onChange={(v) => u({ detail: v })} />
              <AreaField label="Depoimento" rows={3} max={800} value={i.text} onChange={(v) => u({ text: v })} />
              <Choice label="Estrelas" value={i.rating} onChange={(v) => u({ rating: v })} options={[0, 3, 4, 5].map((n) => ({ value: n, label: n ? '★'.repeat(n) : 'Sem' }))} />
            </>} />
        </>
      case 'pricing':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <TextField label="Subtítulo" value={block.subtitle} max={400} onChange={(v) => set(block, { subtitle: v })} />
          <ListField label="Opções" items={block.items} max={6} onChange={(v) => set(block, { items: v })} itemTitle={(i) => `${i.name} · ${i.price}`}
            create={() => ({ name: 'Nova opção', price: 'R$ 0', oldPrice: '', description: '', features: [], highlight: false, button: { label: 'Quero esse', action: { kind: 'whatsapp' as const, phone, message: 'Olá! Tenho interesse.' } } })}
            render={(i, u) => <>
              <TextField label="Nome" value={i.name} max={120} onChange={(v) => u({ name: v })} />
              <div className="grid g-2" style={{ gap: 8 }}>
                <TextField label="Preço" value={i.price} max={40} onChange={(v) => u({ price: v })} />
                <TextField label="De (riscado)" value={i.oldPrice} max={40} onChange={(v) => u({ oldPrice: v })} />
              </div>
              <TextField label="Descrição" value={i.description} max={300} onChange={(v) => u({ description: v })} />
              <LinesField label="O que inclui" value={i.features} onChange={(v) => u({ features: v })} />
              <Toggle label="Destacar como mais escolhido" value={i.highlight} onChange={(v) => u({ highlight: v })} />
              <ButtonField label="Botão" optional defaultPhone={phone} value={i.button} onChange={(v) => u({ button: v })} />
            </>} />
        </>
      case 'stats':
        return <>
          <TextField label="Título (opcional)" value={block.title} onChange={(v) => set(block, { title: v })} />
          <ListField label="Números" items={block.items} max={6} onChange={(v) => set(block, { items: v })} itemTitle={(i) => `${i.value} ${i.label}`}
            create={() => ({ value: '100+', label: 'clientes' })}
            render={(i, u) => <>
              <TextField label="Número" value={i.value} max={30} onChange={(v) => u({ value: v })} />
              <TextField label="Legenda" value={i.label} max={80} onChange={(v) => u({ label: v })} />
            </>} />
        </>
      case 'faq':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <ListField label="Perguntas" items={block.items} onChange={(v) => set(block, { items: v })} itemTitle={(i) => i.q}
            create={() => ({ q: 'Nova pergunta?', a: 'Resposta.' })}
            render={(i, u) => <>
              <TextField label="Pergunta" value={i.q} max={300} onChange={(v) => u({ q: v })} />
              <AreaField label="Resposta" rows={3} max={1500} value={i.a} onChange={(v) => u({ a: v })} />
            </>} />
        </>
      case 'cta':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <AreaField label="Texto" rows={2} max={500} value={block.text} onChange={(v) => set(block, { text: v })} />
          <ButtonField label="Botão" defaultPhone={phone} value={block.button} onChange={(v) => set(block, { button: v })} />
        </>
      case 'form': {
        const f = block.fields
        const labels = { name: 'Nome', phone: 'WhatsApp / telefone', email: 'E-mail', message: 'Mensagem' } as const
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <AreaField label="Texto" rows={2} max={500} value={block.text} onChange={(v) => set(block, { text: v })} />
          <div className="field">
            <label>Campos</label>
            <div className="sub-card stack" style={{ gap: 8 }}>
              {(Object.keys(labels) as Array<keyof typeof labels>).map((k) => (
                <div key={k} className="row between" style={{ gap: 8 }}>
                  <label className="row small" style={{ gap: 6 }}><input type="checkbox" checked={f[k].enabled} onChange={(e) => set(block, { fields: { ...f, [k]: { ...f[k], enabled: e.target.checked, required: e.target.checked && f[k].required } } })} /> {labels[k]}</label>
                  {f[k].enabled && <label className="row small muted" style={{ gap: 6 }}><input type="checkbox" checked={f[k].required} onChange={(e) => set(block, { fields: { ...f, [k]: { ...f[k], required: e.target.checked } } })} /> obrigatório</label>}
                </div>
              ))}
            </div>
          </div>
          <TextField label="Texto do botão" value={block.submitLabel} max={60} onChange={(v) => set(block, { submitLabel: v })} />
          <AreaField label="Mensagem depois de enviar" rows={2} max={300} value={block.successMessage} onChange={(v) => set(block, { successMessage: v })} />
          <TextField label="Depois de enviar, abrir o WhatsApp (opcional)" inputMode="tel" placeholder="(11) 99999-9999" value={block.whatsappAfter} max={30} onChange={(v) => set(block, { whatsappAfter: v })} hint="Se preencher, a pessoa já cai na sua conversa depois de mandar os dados." />
          <TextField label="Aviso de privacidade" value={block.consentText} max={300} onChange={(v) => set(block, { consentText: v })} />
          <p className="small muted">Os contatos chegam em Páginas → Contatos recebidos, e você recebe um aviso.</p>
        </>
      }
      case 'video':
        return <>
          <TextField label="Título (opcional)" value={block.title} onChange={(v) => set(block, { title: v })} />
          <TextField label="Link do vídeo" inputMode="url" value={block.url} max={500} placeholder="https://www.youtube.com/watch?v=…" onChange={(v) => set(block, { url: v })} hint="YouTube ou Vimeo." />
        </>
      case 'countdown': {
        const local = toLocalInput(block.endsAt)
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <TextField label="Termina em" type="datetime-local" value={local} onChange={(v) => { const d = new Date(v); if (!isNaN(d.getTime())) set(block, { endsAt: d.toISOString() }) }} />
          <TextField label="Texto quando acabar" value={block.expiredText} max={120} onChange={(v) => set(block, { expiredText: v })} />
        </>
      }
      case 'map':
        return <>
          <TextField label="Título" value={block.title} onChange={(v) => set(block, { title: v })} />
          <TextField label="Endereço" value={block.address} max={300} placeholder="Rua, número, bairro, cidade" onChange={(v) => set(block, { address: v })} />
        </>
      case 'spacer':
        return <>
          <Choice label="Tamanho" value={block.size} onChange={(v) => set(block, { size: v })} options={[{ value: 'sm', label: 'Pequeno' }, { value: 'md', label: 'Médio' }, { value: 'lg', label: 'Grande' }]} />
          <Toggle label="Mostrar linha" value={block.line} onChange={(v) => set(block, { line: v })} />
        </>
      case 'footer':
        return <>
          <TextField label="Texto" value={block.text} max={300} onChange={(v) => set(block, { text: v })} />
          <TextField label="Endereço" value={block.address} max={300} onChange={(v) => set(block, { address: v })} />
          <TextField label="Telefone" inputMode="tel" value={block.phone} max={30} onChange={(v) => set(block, { phone: v })} />
          <TextField label="Instagram" value={block.instagram} max={60} placeholder="@seuperfil" onChange={(v) => set(block, { instagram: v })} />
          <Toggle label="Aviso sobre uso dos dados" value={block.showPrivacy} onChange={(v) => set(block, { showPrivacy: v })} />
        </>
    }
  })()
  return (
    <div className="stack" style={{ gap: 14 }}>
      {body}
      {block.type !== 'spacer' && <BgField value={block.bg} onChange={(v) => onChange({ ...block, bg: v })} />}
    </div>
  )
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
